<template>
  <div class="onboarding-enter-data-shell d-flex flex-column">
    <v-row
      class="onboarding-enter-data-layout flex-row flex-grow-1 min-h-0"
      align="stretch"
    >
      <v-col
        v-if="showAssistantChat"
        cols="12"
        :md="showFormColumn ? 5 : 12"
        class="onboarding-enter-data-chat d-flex flex-column min-h-0"
      >
        <AiAssistantDrawer
          class="w-100 flex-grow-1 min-h-0"
          embedded
        />
      </v-col>
      <v-col
        v-if="showFormColumn"
        cols="12"
        :md="showAssistantChat ? 7 : 12"
        class="onboarding-enter-data-form d-flex flex-column min-h-0"
      >
        <OnboardingDataEntry
          :form-data="formData"
          :wizard-company="company"
          :ai-captured-fields="aiCapturedFields"
          :form-hidden="false"
          :continue-label="continueLabel"
          :is-us-onboarding="isUsOnboarding"
          :has-no-website="hasNoWebsite"
          :legal-consents="legalConsents"
          @update:field="(field, value) => emit('update:field', field, value)"
          @update:legal-consents="emit('update:legalConsents', $event)"
          @image-uploaded="emit('image-uploaded', $event)"
          @widget-integration="emit('widget-integration', $event)"
          @back="emit('back')"
          @continue="emit('continue')"
        />
      </v-col>
    </v-row>
    <div
      v-if="showAssistantChat || canSwitchToAssistant"
      class="onboarding-enter-data-mode-link text-center"
    >
      <button
        v-if="showAssistantChat"
        type="button"
        class="onboarding-enter-data-mode-link__btn"
        @click="emit('switch-to-manual')"
      >
        {{ $t('onboarding.wizard.data-entry.show-form') }}
      </button>
      <button
        v-else
        type="button"
        class="onboarding-enter-data-mode-link__btn"
        @click="emit('switch-to-assistant')"
      >
        {{ $t('onboarding.wizard.data-entry.show-chat') }}
      </button>
    </div>
    <div
      v-if="showAssistantChat"
      class="d-flex flex-wrap justify-space-between align-center ga-3 onboarding-enter-data-actions"
    >
      <v-btn
        variant="outlined"
        rounded="lg"
        @click="emit('back')"
      >
        {{ $t('onboarding.stepContainer.back') }}
      </v-btn>
      <v-btn
        color="primary"
        rounded="lg"
        size="large"
        class="onboarding-enter-data-actions__continue"
        @click="emit('continue')"
      >
        {{ continueLabel }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LegalConsentsValues } from '@/components/ai/onboarding/OnboardingLegalConsents.vue'
import AiAssistantDrawer from '@/components/AiAssistantDrawer.vue'

import type { OnboardingFormData } from '../lib/onboardingPrefill'
import type { OnboardingWizardCompany } from '../lib/onboardingWizardCompany'
import OnboardingDataEntry from '../OnboardingDataEntry.vue'

defineProps<{
  formData: OnboardingFormData
  company: OnboardingWizardCompany | null
  aiCapturedFields: Set<string>
  legalConsents: LegalConsentsValues
  showAssistantChat: boolean
  showFormColumn: boolean
  canSwitchToAssistant: boolean
  continueLabel: string
  /** Shared, session-aware US-market flag threaded down from `useOnboardingForm` (index.vue). */
  isUsOnboarding: boolean
  /** User ticked "I don't have a website" — the website field is hidden. */
  hasNoWebsite: boolean
}>()

const emit = defineEmits<{
  'update:field': [field: string, value: string | boolean]
  'update:legalConsents': [value: LegalConsentsValues]
  'image-uploaded': [payload: { kind: 'logo' | 'team' | 'profile'; url?: string }]
  'widget-integration': [payload: { enabled: boolean }]
  back: []
  continue: []
  'switch-to-manual': []
  'switch-to-assistant': []
}>()
</script>

<style scoped>
.onboarding-enter-data-layout {
  flex: 1 1 0;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
}

.onboarding-enter-data-layout > .v-col {
  min-height: 0;
  max-height: 100%;
}

.onboarding-enter-data-form {
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 32px;
}

.onboarding-enter-data-shell {
  flex: 1 1 0;
  min-height: 0;
  max-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.onboarding-enter-data-chat {
  flex: 1 1 0;
  min-height: 0;
  max-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.onboarding-enter-data-actions {
  position: relative;
  z-index: 1300;
  flex-shrink: 0;
  width: 100%;
  padding-top: 16px;
  padding-bottom: 4px;
}

/* Sit above the ProductFruits "Support & Tipps" launcher (z-index 1200). */
.onboarding-enter-data-actions__continue {
  position: relative;
  z-index: 1300;
}

.onboarding-enter-data-mode-link {
  flex-shrink: 0;
  width: 100%;
  padding: 16px 0 8px;
}

.onboarding-enter-data-mode-link__btn {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.onboarding-enter-data-mode-link__btn:hover {
  opacity: 0.88;
}

.onboarding-enter-data-chat :deep(.ai-drawer-embedded) {
  position: static;
  top: auto;
  flex: 1 1 0;
  min-height: 0;
  max-height: 100%;
  height: 100%;
  overflow: hidden;
}

.onboarding-enter-data-chat :deep(.ai-chat-body) {
  flex: 1 1 0;
  min-height: 0;
  max-height: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
